import React, { useEffect, useMemo, useState } from "react";
import { formatQuantity } from "../../../../helpers/functions/formatQuantity";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Input,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";

import { Link, useParams } from "react-router-dom";

import { isEmpty } from "lodash";
import TableContainer from "../../../../Components/Common/TableContainer";
import {
  deleteBillableJobItem,
  deleteBillableJobItemSuccess,
  getBillableJobItemDetails,
  getBillableJobItemDetailsSuccess,
  getBillableJobItemsList,
  readSingleJob,
  updateBillableJobItemSuccess,
} from "../../../../store/actions";
import HandleDateTime from "../../../../Components/Common/HandleDateTime";
import CreateEditBillableItems from "../../../../Components/popUpComponents/CreateEditBillableItems";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import { toast } from "react-toastify";
import {
  reorderJobInvoiceItemsApiCall,
  updateBillableJobItemApiCall,
  jobInvoiceItemAiGenerateApiCall,
} from "../../../../helpers/fakebackend_helper";
import AppModal from "../../../../Components/Common/AppModal";

// The amount columns are DECIMAL(20,6) in the database, so the API hands back
// "12.500000" and printing it raw put six decimal places on screen (Task 24532).
// Money is money: two places, thousands separated.
const money = (v) => {
  if (v === null || v === undefined || v === "") return null;
  return `£${Number(v).toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export default function InvoiceItems({ Customer_Id, jobId }) {
  const dispatch = useDispatch();
  const params = useParams();
  // When rendered outside the single-job route (e.g. inside the timer
  // completion modal) the Job id is passed in as a prop; on the job page it
  // comes from the route params.
  const effectiveJobId = jobId ?? params.id;

  // Redux States

  const { filtersData } = useSelector((state) => ({
    filtersData: state.Filters.filtersData,
  }));
  const { billableJobItemDetailsAPI } = useSelector((state) => ({
    billableJobItemDetailsAPI:
      state.SingleJob.billableJobItemDetails?.jobinvoiceitem_data,
  }));

  // Local States
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResultData, setSearchResultData] = useState([]);
  const [allowedSearchFilters, setAllowedSearchFilters] = useState([
    "Id",
    "Product_Name",
    "AmountLocalEach",
    "AmountLocalSubTotal",
    "AmountLocalTotal",
  ]);

  useEffect(() => {
    dispatch(
      getBillableJobItemsList([
        {
          job_data: {
            Id: effectiveJobId,
          },
        },
      ])
    );
  }, [effectiveJobId]);

  const { ListData } = useSelector((state) => ({
    ListData: state.SingleJob.billableJobItemsList?.jobinvoiceitem_list,
  }));

  const [reordering, setReordering] = useState(false);

  const refreshList = () => {
    dispatch(getBillableJobItemsList([{ job_data: { Id: effectiveJobId } }]));
  };

  // Move a line up or down. The rows move locally first so the table does not
  // jump about mid-request, then the WHOLE list is sent in its new order and the
  // endpoint renumbers from 1 - which also cleans up the rows created before
  // Order was being set. Disabled while a search is filtering the table, because
  // "up" against a filtered view would mean something different from what it
  // looks like.
  // Task 24805 - where a row actually sits in the data, found by its id.
  //
  // The arrows used to take react-table's row.index, which is a position in
  // whatever the table is currently DISPLAYING. With the default sort that was
  // a different sequence entirely, and even with the sort removed it would go
  // wrong again the moment somebody clicked a column header. An id cannot be
  // reordered out from under us, so the swap is computed from that instead.
  const positionOf = (rowId) =>
    (searchResultData || []).findIndex(
      (row) => String(row?.Id) === String(rowId)
    );

  const handleMove = async (rowId, direction) => {
    if (reordering) return;

    const index = positionOf(rowId);
    if (index < 0) return;

    const target = index + direction;
    if (target < 0 || target >= (searchResultData?.length ?? 0)) return;

    const next = [...searchResultData];
    [next[index], next[target]] = [next[target], next[index]];
    setSearchResultData(next);

    setReordering(true);
    try {
      const res = await reorderJobInvoiceItemsApiCall([
        {
          Job_Id: effectiveJobId,
          JobInvoiceItem_Ids: next.map((row) => row.Id),
        },
      ]);
      if (res?.status === 200) {
        refreshList();
      } else {
        toast.error(res?.message || "Could not save the new order");
        refreshList();
      }
    } catch (err) {
      toast.error("Could not save the new order");
      refreshList();
    } finally {
      setReordering(false);
    }
  };

  useEffect(() => {
    let filteredApiData;
    if (isEmpty(ListData)) {
      filteredApiData = [];
    } else {
      filteredApiData = ListData;
    }
    // Deliberately NOT re-sorted by Id: /jobinvoiceitem/search returns the rows
    // in `Order` sequence and that order is what the invoice conversion reads,
    // so the tab has to show the same sequence the customer will see on the
    // invoice (Task 24524). Re-sorting here silently threw it away.
    const timer = setTimeout(() => {
      // Search Filter

      // Search Query Filter
      filteredApiData = filteredApiData?.filter((item) => {
        return allowedSearchFilters?.some((key) => {
          return (
            item[key] &&
            item[key]
              ?.toString()
              ?.toLowerCase()
              ?.includes(searchQuery?.toLowerCase())
          );
        });
      });
      setSearchResultData(filteredApiData);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, filtersData, ListData]);

  // Task 24794 - ignore / un-ignore a line.
  //
  // This tab is where an ignored line still lives, which is the whole point of
  // ignoring rather than deleting: the work stays on the job's record, it just
  // stops being offered on every interim invoice and stops counting toward the
  // job's outstanding amount. So the decision has to be visible here, and it
  // has to be reversible - otherwise it is a delete with extra steps.
  const [togglingIgnoreId, setTogglingIgnoreId] = useState(null);

  // Change #100206 - AI-written title / description for one line. The server
  // only suggests; the text is shown here, can be edited, and is saved only
  // when accepted.
  const [aiRow, setAiRow] = useState(null);
  const [aiLoadingId, setAiLoadingId] = useState(null);
  const [aiDraft, setAiDraft] = useState({ Title: "", Description: "" });
  const [aiSaving, setAiSaving] = useState(false);
  const handleAiGenerate = async (row) => {
    if (!row?.Id || aiLoadingId) return;
    setAiLoadingId(row.Id);
    try {
      const res = await jobInvoiceItemAiGenerateApiCall([{ Id: row.Id }]);
      if (res?.status !== 200) {
        toast.error(res?.message || "Could not generate the wording");
        return;
      }
      setAiDraft({
        Title: res.Title || row.Title || "",
        Description: res.Description || row.Description || "",
      });
      setAiRow(row);
    } catch (err) {
      toast.error("Could not generate the wording");
    } finally {
      setAiLoadingId(null);
    }
  };
  const handleAiAccept = async () => {
    if (!aiRow || aiSaving) return;
    setAiSaving(true);
    try {
      const res = await updateBillableJobItemApiCall([
        { Id: aiRow.Id, Title: aiDraft.Title, Description: aiDraft.Description },
      ]);
      if (res?.status === 200) {
        toast.success("Line updated with the AI wording");
        setAiRow(null);
        refreshList();
      } else {
        toast.error(res?.message || "Could not save the wording");
      }
    } catch (err) {
      toast.error("Could not save the wording");
    } finally {
      setAiSaving(false);
    }
  };

  const handleToggleIgnore = async (row) => {
    if (togglingIgnoreId) return;
    const nowIgnored = row?.FlagIgnored == 1;
    setTogglingIgnoreId(row?.Id);
    try {
      const res = await updateBillableJobItemApiCall([
        { Id: row?.Id, FlagIgnored: nowIgnored ? 0 : 1 },
      ]);
      if (res?.status === 200) {
        toast.success(nowIgnored ? "Line restored" : "Line ignored");
        refreshList();
        // The job's outstanding amount is recalculated server side on the
        // update, so the header has to be re-read or it keeps showing the
        // old total.
        dispatch(readSingleJob({ id: effectiveJobId }));
      } else {
        toast.error(res?.message || "Could not update this line");
      }
    } catch (err) {
      toast.error("Could not update this line");
    } finally {
      setTogglingIgnoreId(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        // The order these lines are billed in - it carries through to the
        // invoice, so it is worth being able to set (Task 24524).
        Header: "Order",
        id: "order",
        filterable: false,
        Cell: (item) => {
          const rowId = item.row.original?.Id;
          const index = positionOf(rowId);
          const searching = Boolean(searchQuery?.trim());
          const last = (searchResultData?.length ?? 0) - 1;
          return (
            <div className="d-flex flex-column">
              <button
                type="button"
                className="btn btn-sm btn-ghost-secondary border-0 p-0 lh-1"
                title={
                  searching
                    ? "Clear the search to reorder"
                    : "Move up"
                }
                disabled={reordering || searching || index <= 0}
                onClick={() => handleMove(rowId, -1)}
              >
                <i className="ri-arrow-up-s-line fs-16" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-ghost-secondary border-0 p-0 lh-1"
                title={
                  searching
                    ? "Clear the search to reorder"
                    : "Move down"
                }
                disabled={reordering || searching || index < 0 || index === last}
                onClick={() => handleMove(rowId, 1)}
              >
                <i className="ri-arrow-down-s-line fs-16" />
              </button>
            </div>
          );
        },
      },
      {
        // job id  with beginning #Job_Id
        Header: "Id",
        accessor: "Id",
        filterable: false,
        Cell: (item) => (
          <>
            <span>#{item.row.original?.Id}</span>
          </>
        ),
      },

      {
        Header: "Product",
        accessor: "Product_Name",
      },
      {
        Header: "Title",
        accessor: "Title",
      },
      {
        Header: "Description",
        accessor: "Description",
      },
      {
        Header: "Qty",
        accessor: "Quantity",
        // Also DECIMAL(20,6): show what was entered, not "2.000000" (Task
        // 24532). Task 24808 - through the shared helper now, and capped at
        // two places rather than three, which is the agreed rule.
        Cell: (item) => formatQuantity(item.row.original?.Quantity),
      },
      {
        Header: () => {
          return (
            <div className="text-end">
              <span>Each (excl VAT)</span>
            </div>
          );
        },
        accessor: "AmountLocalEach",
        //accessor status is  joblist.JobStatus_Id jobStatus.ID jobStatus.Name
        Cell: (customer) => {
          return (
            <div className="text-end">
              <span className={""}>
                {money(customer.row.original?.AmountLocalEach)}
              </span>
            </div>
          );
        },
        filterable: true,
      },
      {
        Header: () => (
          <div className="text-end">
            <span>Sub-total (excl VAT)</span>
          </div>
        ),
        accessor: "AmountLocalSubTotal",
        //accessor status is  joblist.JobStatus_Id jobStatus.ID jobStatus.Name
        Cell: (customer) => {
          return (
            <div className="text-end">
              <span className={""}>
                {money(customer.row.original?.AmountLocalSubTotal)}
              </span>
            </div>
          );
        },
        filterable: false,
      },
      {
        Header: () => (
          <div className="text-end">
            <span>VAT</span>
          </div>
        ),
        accessor: "AmountLocalVat",
        Cell: (customer) => {
          return (
            <div className="text-end">
              <span>{money(customer.row.original?.AmountLocalVat)}</span>
            </div>
          );
        },
        filterable: false,
      },
      {
        Header: () => (
          <div className="text-end">
            <span>Total (inc VAT)</span>
          </div>
        ),
        accessor: "AmountLocalTotal",
        //accessor status is  joblist.JobStatus_Id jobStatus.ID jobStatus.Name
        Cell: (customer) => {
          return (
            <div className="text-end">
              <span className={""}>
                {money(customer.row.original?.AmountLocalTotal)}
              </span>
            </div>
          );
        },
        filterable: false,
      },
      {
        // Where a line was converted from a purchase, show it and link back to
        // the purchase. The linkage lives on PurchaseItem.JobInvoiceItem_Id, not
        // on this row, so /jobinvoiceitem/search joins it in from that side.
        // Lines that came from a time entry or were typed by hand show nothing.
        Header: "From purchase",
        accessor: "PurchaseItem_Id",
        filterable: false,
        Cell: (item) => {
          const row = item.row.original;
          if (!row?.PurchaseItem_Id) {
            return <span className="text-muted">-</span>;
          }
          const cost = Number(row?.PurchaseItem_AmountLocalSubTotal);
          const charge = Number(row?.AmountLocalSubTotal);
          const hasMargin = Number.isFinite(cost) && Number.isFinite(charge);
          const margin = hasMargin ? charge - cost : null;
          // Margin ON THE SALE, i.e. margin / charge - the usual gross-margin
          // reading, not markup on cost (Task 24530). A zero-value line has no
          // meaningful percentage, so it shows none rather than 0% or Infinity.
          const marginPercent =
            hasMargin && charge !== 0 ? (margin / charge) * 100 : null;
          return (
            <div className="d-flex flex-column align-items-start">
              <Link
                to={`/purchase/${row?.PurchaseItem_Purchase_Id}/view`}
                title={`Purchase item #${row?.PurchaseItem_Id}`}
              >
                <span className="badge bg-soft-primary text-primary">
                  Purchase #{row?.PurchaseItem_Purchase_Id}
                </span>
              </Link>
              {row?.PurchaseItem_Supplier_FullName && (
                <span className="text-muted small">
                  {row?.PurchaseItem_Supplier_FullName}
                </span>
              )}
              {hasMargin && (
                <span
                  className={`small ${
                    margin < 0 ? "text-danger" : "text-success"
                  }`}
                  title="Charge less what the purchase cost. The percentage is margin as a share of the charge (gross margin), not markup on cost."
                >
                  Cost {money(cost)} / margin {money(margin)}
                  {marginPercent === null ? "" : ` (${marginPercent.toFixed(1)}%)`}
                </span>
              )}
            </div>
          );
        },
      },
      {
        Header: "Status",
        accessor: "FlagInvoiced",
        filterable: false,
        Cell: (item) => {
          const row = item.row.original;
          const billed = row?.FlagInvoiced == 1;
          const proformaBilled = row?.FlagProformaInvoiced == 1;
          if (billed) {
            return <span className="badge bg-secondary">Billed</span>;
          }
          // Task 24794 - checked after Billed, because a line that somehow ended
          // up both should read as what actually happened to it, not as what
          // somebody meant to happen.
          if (row?.FlagIgnored == 1) {
            return (
              <span
                className="badge bg-warning-subtle text-warning"
                title="Ignored - kept on the job but never billed"
              >
                Ignored
              </span>
            );
          }
          if (proformaBilled) {
            return <span className="badge bg-info">Proforma</span>;
          }
          return (
            <span className="badge bg-success-subtle text-success">
              Remaining
            </span>
          );
        },
      },
      {
        Header: "Last Updated",
        accessor: (row) => {
          //CreatedDateTime sort date so its day Month name Year and time

          return (
            <div>
              {row?.UpdatedDateTime && (
                <div>{HandleDateTime(row.UpdatedDateTime)} </div>
              )}
            </div>
          );

          // return row.CreatedDateTime
        },
        filterable: false,
      },
      {
        Header: "Action",
        Cell: (cellProps) => {
          const row = cellProps.row.original;
          return (
            <div className="d-flex flex-row gap-1 justify-content-end">
              <button
                type="button"
                className="btn btn-sm btn-soft-primary"
                title="Edit this line"
                onClick={() => handleEditSingleBillableItem(row?.Id)}
              >
                <i className="ri-pencil-fill align-bottom" />
              </button>
              {/* Change #100206 - AI title and description, as on convert. */}
              {String(row?.FlagInvoiced) !== "1" && String(row?.FlagProformaInvoiced) !== "1" && (
                <button
                  type="button"
                  className="btn btn-sm btn-soft-info"
                  title="AI - write the title and description from the job"
                  disabled={aiLoadingId === row?.Id}
                  onClick={() => handleAiGenerate(row)}
                >
                  {aiLoadingId === row?.Id ? (
                    <span className="spinner-border spinner-border-sm" />
                  ) : (
                    <i className="ri-magic-line align-bottom" />
                  )}
                </button>
              )}
              {/* Task 24794 - only for a line that is still billable; a
                  billed one is settled and ignoring it would say nothing
                  true. */}
              {row?.FlagInvoiced != 1 && row?.FlagProformaInvoiced != 1 && (
                <button
                  type="button"
                  className={
                    row?.FlagIgnored == 1
                      ? "btn btn-sm btn-soft-warning"
                      : "btn btn-sm btn-soft-secondary"
                  }
                  title={
                    row?.FlagIgnored == 1
                      ? "Restore this line - it will be billable again"
                      : "Ignore this line - keep it on the job but never bill it"
                  }
                  disabled={togglingIgnoreId === row?.Id}
                  onClick={() => handleToggleIgnore(row)}
                >
                  <i
                    className={
                      row?.FlagIgnored == 1
                        ? "ri-eye-line align-bottom"
                        : "ri-eye-off-line align-bottom"
                    }
                  />
                </button>
              )}
              <button
                type="button"
                className="btn btn-sm btn-soft-danger"
                title="Void this line"
                onClick={() => {
                  setSelectedForDelete([row?.Id]);
                  toggleDeleteModal();
                }}
              >
                <i className="ri-delete-bin-fill align-bottom" />
              </button>
            </div>
          );
        },
      },
    ],
    // reordering / searchQuery drive the up-down buttons' disabled state, and
    // the move handler closes over the current rows, so the columns have to be
    // rebuilt when any of them change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchResultData, reordering, searchQuery, togglingIgnoreId, aiLoadingId]
  );
  // Task 24809 - open a line for editing.
  //
  // The modal is fed from shared redux state, and it seeds its form in an
  // effect keyed on that value. Two consequences if the previous row is left
  // sitting there:
  //
  //   * opening a DIFFERENT row flashes the last one's figures until the fetch
  //     lands, which on a slow call is long enough to start typing into;
  //   * reopening the SAME row does not re-seed at all - the value is
  //     unchanged, so the effect never re-runs and the form still holds
  //     whatever was last typed into it rather than what is actually saved.
  //
  // Clearing first makes the modal always start from a fresh fetch. Same fix
  // the diary-rule wizard needed for the same reason (Task 24629).
  const handleEditSingleBillableItem = (id) => {
    dispatch(getBillableJobItemDetailsSuccess({}));
    dispatch(getBillableJobItemDetails({ id }));
    handleCreateUpdateData("update");
  };

  const handleJobSearch = (e) => {
    e.preventDefault();
    setSearchQuery(e.target.value);
  };
  const [modalBillableItems, setModalBillableItems] = useState(false);
  const toggleBillableItems = () => {
    setModalBillableItems((open) => {
      // Task 24809 - drop the loaded row on the way out as well as on the way
      // in, so a closed modal never leaves a line's figures in shared state
      // for something else to pick up.
      if (open) dispatch(getBillableJobItemDetailsSuccess({}));
      return !open;
    });
  };
  const [isEdit, setIsEdit] = useState(false);
  const handleCreateUpdateData = (type) => {
    if (type === "create") {
      setIsEdit(false);
    }
    if (type === "update") {
      setIsEdit(true);
    }
    setModalBillableItems(true);
  };
  // Search filter ends
  const [selectedForDelete, setSelectedForDelete] = useState([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const toggleDeleteModal = () => setIsDeleteModalOpen(!isDeleteModalOpen);
  const handleDeleteItems = () => {
    dispatch(deleteBillableJobItem(selectedForDelete));
  };
  const { billableJobItemDeleteSuccessAPI } = useSelector((state) => ({
    billableJobItemDeleteSuccessAPI:
      state.SingleJob.billableJobItemDeleteSuccess,
  }));

  useEffect(() => {
    if (isEmpty(billableJobItemDeleteSuccessAPI)) return;
    if (billableJobItemDeleteSuccessAPI) {
      toast.success(billableJobItemDeleteSuccessAPI.message);
      dispatch(
        getBillableJobItemsList([
          {
            job_data: {
              Id: effectiveJobId,
            },
          },
        ])
      );
      if (effectiveJobId) {
        dispatch(readSingleJob({ id: effectiveJobId }));
      }
      setIsDeleteModalOpen(false);
    } else {
      toast.error(billableJobItemDeleteSuccessAPI.message);
    }
    dispatch(deleteBillableJobItemSuccess([]));
  }, [billableJobItemDeleteSuccessAPI]);

  // Task 24807 - one "Total Amount" said nothing useful. It added up every
  // line whatever its state, so a job that was half billed showed a number
  // that matched neither what had gone out nor what was still to go.
  //
  // Split three ways, each net / VAT / gross, because those are the three
  // questions actually being asked of this screen: what has been billed, what
  // has not, and what is deliberately never going to be.
  //
  // A line is INVOICED on FlagInvoiced only. A proforma is not an invoice - it
  // is a request for payment against work still to be billed - so a
  // proforma'd line stays in "not yet invoiced", which is where the money
  // still is.
  //
  // IGNORED lines (Task 24794) are pulled out into their own row rather than
  // dropped, so the three rows still add up to the grand total and nobody has
  // to wonder where the missing pounds went.
  const billableTotals = useMemo(() => {
    const blank = () => ({ net: 0, vat: 0, gross: 0 });
    const out = {
      invoiced: blank(),
      uninvoiced: blank(),
      ignored: blank(),
      all: blank(),
    };

    (searchResultData || []).forEach((row) => {
      const net = Number(row?.AmountLocalSubTotal) || 0;
      const vat = Number(row?.AmountLocalVat) || 0;
      const gross = Number(row?.AmountLocalTotal) || 0;

      const bucket =
        String(row?.FlagIgnored) === "1"
          ? out.ignored
          : String(row?.FlagInvoiced) === "1"
          ? out.invoiced
          : out.uninvoiced;

      bucket.net += net;
      bucket.vat += vat;
      bucket.gross += gross;

      out.all.net += net;
      out.all.vat += vat;
      out.all.gross += gross;
    });

    return out;
  }, [searchResultData]);

  const hasIgnoredLines = billableTotals.ignored.gross !== 0;
  return (
    <React.Fragment>
      {/* Change #100206 - review the AI wording before it is saved. */}
      <AppModal
        open={!!aiRow}
        onClose={() => !aiSaving && setAiRow(null)}
        title={`AI wording - line #${aiRow?.Id || ""}`}
        onSubmit={handleAiAccept}
        submitLabel={aiSaving ? "Saving..." : "Use this wording"}
        submitDisabled={aiSaving}
        size="md"
      >
        <label className="form-label">Title</label>
        <Input
          type="text"
          className="mb-3"
          value={aiDraft.Title}
          onChange={(e) => setAiDraft((p) => ({ ...p, Title: e.target.value }))}
        />
        <label className="form-label">Description</label>
        <Input
          type="textarea"
          rows={6}
          value={aiDraft.Description}
          onChange={(e) => setAiDraft((p) => ({ ...p, Description: e.target.value }))}
        />
        <div className="form-text">
          Written from the job's details and notes. Edit it if you like - nothing
          is saved until you choose "Use this wording".
        </div>
      </AppModal>
      {isDeleteModalOpen && (
        <DeleteModal
          show={isDeleteModalOpen}
          onDeleteClick={() => {
            handleDeleteItems();
          }}
          onCloseClick={() => {
            toggleDeleteModal();
          }}
        />
      )}

      {modalBillableItems && (
        <CreateEditBillableItems
          isEdit={isEdit}
          setIsEdit={setIsEdit}
          modal={modalBillableItems}
          toggle={toggleBillableItems}
          setModal={setModalBillableItems}
          Job_Id={effectiveJobId}
          updateData={billableJobItemDetailsAPI}
          Customer_Id={Customer_Id}
        />
      )}

      <Row>
        <Col lg={12}>
          <Card id="customersList">
            <CardHeader className="border-0">
              <h3>Billable Items List</h3>
              <Row className="g-4 align-items-center">
                <Col sm={3}>
                  <div className="search-box">
                    <Input
                      // create search with jobs List Data
                      type="search"
                      className="form-control search"
                      placeholder="Search for..."
                      onChange={handleJobSearch}
                      value={searchQuery}
                    />
                    <i className="ri-search-line search-icon"></i>
                  </div>
                </Col>
                <div className="col-sm-auto ms-auto">
                  <div className="hstack gap-2 ">
                    <button
                      type="button"
                      className="btn btn-success btn-info-filters d-flex align-items-center"
                      onClick={() => {
                        handleCreateUpdateData("create");
                      }}
                    >
                      {/*plus icon*/}
                      <i className="ri-add-line me-2  "></i>
                      Add Billable Item
                    </button>
                  </div>
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <div>
                {!isEmpty(searchResultData) ? (
                  <div>
                    <div>
                      <TableContainer
                        columns={columns}
                        data={searchResultData || []}
                        // Task 24805 - TableContainer defaults to
                        // initialSortBy [{id:"Id", desc:true}], and this tab
                        // never overrode it, so the rows rendered newest-id
                        // first while searchResultData held them in Order
                        // sequence. Two things were wrong because of it: the
                        // list was not showing the order the invoice will
                        // actually use (which this tab exists to control), and
                        // the reorder arrows read row.index - a DISPLAY index -
                        // against the unsorted array, so they disabled on the
                        // wrong rows and swapped the wrong pair. Empty, so the
                        // rows stay in the order the API sent them.
                        initialSortBy={[]}
                        isGlobalFilter={false}
                        isAddUserList={false}
                        customPageSize={10}
                        className="custom-header-css"
                        divClass="table-responsive table-card mb-0"
                        tableClass="align-middle table-wrap"
                        theadClass="text-muted"
                        isLeadsFilter={false}
                      />
                    </div>
                    {/* Task 24807 - the split. Right-aligned under the table
                        where the single total used to be. */}
                    <div className="d-flex justify-content-end p-2">
                      <table className="table table-sm w-auto mb-0 job-billable-totals">
                        <thead>
                          <tr className="text-muted">
                            <th></th>
                            <th className="text-end">Excl VAT</th>
                            <th className="text-end">VAT</th>
                            <th className="text-end">Inc VAT</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td className="text-muted">Invoiced</td>
                            <td className="text-end">
                              {money(billableTotals.invoiced.net)}
                            </td>
                            <td className="text-end">
                              {money(billableTotals.invoiced.vat)}
                            </td>
                            <td className="text-end">
                              {money(billableTotals.invoiced.gross)}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted">Not yet invoiced</td>
                            <td className="text-end">
                              {money(billableTotals.uninvoiced.net)}
                            </td>
                            <td className="text-end">
                              {money(billableTotals.uninvoiced.vat)}
                            </td>
                            <td className="text-end">
                              {money(billableTotals.uninvoiced.gross)}
                            </td>
                          </tr>
                          {hasIgnoredLines && (
                            <tr className="text-muted">
                              <td>
                                Ignored
                                <span className="ms-1 small">
                                  (never billable)
                                </span>
                              </td>
                              <td className="text-end">
                                {money(billableTotals.ignored.net)}
                              </td>
                              <td className="text-end">
                                {money(billableTotals.ignored.vat)}
                              </td>
                              <td className="text-end">
                                {money(billableTotals.ignored.gross)}
                              </td>
                            </tr>
                          )}
                          <tr className="fw-bold border-top">
                            <td>Total</td>
                            <td className="text-end">
                              {money(billableTotals.all.net)}
                            </td>
                            <td className="text-end">
                              {money(billableTotals.all.vat)}
                            </td>
                            <td className="text-end">
                              {money(billableTotals.all.gross)}
                            </td>
                          </tr>
                          {/* The number to act on, said once and plainly. It is
                              the "not yet invoiced" gross figure - repeated
                              here because that is the question the screen gets
                              asked, and hunting for it in a grid is not an
                              answer. */}
                          <tr>
                            <td colSpan={3} className="fw-bold text-success">
                              Remaining to invoice (inc VAT)
                            </td>
                            <td className="text-end fw-bold text-success">
                              {money(billableTotals.uninvoiced.gross)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={"text-center"}>
                      <h2>No Billable Items Found</h2>
                    </div>
                  </>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </React.Fragment>
  );
}
